import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import api from "../api"
import { ReactComponent as ArrowIcon } from "../assets/images/icons/ic-arrow.svg"
import { ReactComponent as EmptyExercises } from "../assets/images/illustrations/3D/empty-exam.svg"
import { ReactComponent as DownloadIcon } from "../assets/images/icons/ic-download.svg"
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg"
import { ReactComponent as TestIcon } from "../assets/images/icons/ic-exercise-inactive.svg"
import { Each } from "../common/Each"
import MainContext from "../common/MainContext"
import Button from '../components/Button'
import Card from "../components/cards/Card"
import EmptyContent from '../components/EmptyContent'
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import LessonsCarousel from "../components/LessonsCarousel"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Table from "../components/Table"
import typo from "../typography.module.css"
import { formatDateV2, formatSeconds, formatTimeV2 } from "../utils"
import styles from "./Dashboard.module.css"
import { TestStatus } from "../enums"

const Dashboard = () => {

    const { t } = useTranslation()
    const context = useContext(MainContext)
    const navigate = useNavigate()

    const [lessons, setLessons] = useState([])
    const [lessonsLoading, setLessonsLoading] = useState(false)

    const [submittedExercises, setSubmittedExercises] = useState([])
    const [submittedExercisesLoading, setSubmittedExercisesLoading] = useState(false)
    const [tests, setTests] = useState([])
    const [testsLoading, setTestsLoading] = useState(false)
    const [recordings, setRecordings] = useState([])
    const [recordingsLoading, setRecordingsLoading] = useState(false)
    const [recordingsPage] = useState(0)

    const getTests = useCallback(async () => {
        setTestsLoading(true);
        try {
            const tests = await api.get("/teacher/tests?status=completed&limit=10");
            setTests(tests);
        } catch (e) {
            console.error(e);
        }
        setTestsLoading(false);
    }, []);

    const getSubmittedExercises = useCallback(async () => {
        setSubmittedExercisesLoading(true)
        try {
            const submitted = await api.get(`/teacher/users/exercises`);
            const sorted = submitted.sort((a, b) => new Date(b.medias[0]?.created_at) - new Date(a.medias[0]?.created_at))
            setSubmittedExercises(sorted.slice(0, 10));
        } catch (e) {
            console.error(e);
        }
        setSubmittedExercisesLoading(false)
    }, [])

    const getRecordings = useCallback(async () => {
        setRecordingsLoading(true);
        try {
            const recordings = await api.get(`/teacher/recordings?page=${recordingsPage}&per=10`);
            setRecordings(recordings);
        } catch (e) {
            console.error(e)
        }
        setRecordingsLoading(false);
    }, [recordingsPage])

    useEffect(() => {
        window.scrollTo({
            top: 0,
        })

        const getLessons = async () => {
            setLessonsLoading(true);
            try {
                let lessons = await api.get(`/teacher/lessons?ends_after=${new Date().toISOString()}&limit=16`);
                setLessons(lessons.sort((a, b) => {
                    return new Date(a.starts_at) - new Date(b.starts_at)
                }))
            } catch (e) {
                console.error(e);
            }
            setLessonsLoading(false);
        };

        getLessons()
        getSubmittedExercises()
        getTests()
    }, [getSubmittedExercises, getTests])

    const getTestQuestionsCount = useCallback((test) => {
        if (!test?.content?.innerTests) {
            return 0
        }

        let count = 0
        for (const innerTest of test.content.innerTests) {
            switch (innerTest?.testType) {
                case "single_choice":
                case "multiple_choice":
                    count += 1
                    break
                case "true_false":
                    count += innerTest?.answers?.length > 0 ? innerTest.answers.length : 0
                    break
                case "text_completion":
                    if (innerTest?.words?.length > 0) {
                        count += innerTest.words.reduce((acc, word) => acc + (word.hidden ? 1 : 0), 0)
                    }
                    break
                default:
                    break
            }
        }

        return count
    }, [])

    const downloadSubmittedExercise = useCallback(async (submission) => {
        if (!submission?.medias?.length) {
            return
        }

        for (const media of submission.medias) {
            const link = document.createElement("a")
            link.setAttribute("id", media.id)
            link.setAttribute("href", media.url)
            document.body.appendChild(link)
        }

        for (const media of submission.medias) {
            const element = document.getElementById(media.id)
            await new Promise((r) => setTimeout(r, 300))
            element?.click()
        }
    }, [])

    const completeDashboardTest = useCallback(async (test) => {
        try {
            await api.put(`/teacher/tests/${test.id}`, { status: TestStatus.Completed })
            await getTests()
        } catch (e) {
            console.error(e)
        }
    }, [getTests])

    const deleteDashboardTest = useCallback(async (test) => {
        try {
            await api.delete(`/teacher/tests/${test.id}`)
            await getTests()
        } catch (e) {
            console.error(e)
        }
    }, [getTests])

    const getSubmittedExerciseActions = useCallback((submission) => ([
        {
            label: "Scarica",
            onClick: () => downloadSubmittedExercise(submission),
            disabled: !submission?.medias?.length,
        },
        {
            label: "Correggi",
            onClick: () => navigate(`/exercises/${submission?.exercise?.id}`),
            disabled: !submission?.exercise?.id,
        },
    ]), [downloadSubmittedExercise, navigate])

    const getDashboardTestActions = useCallback((test) => {
        const actions = [
            {
                label: t("tests.edit"),
                icon: EditIcon,
                style: { color: "var(--primary)" },
                onClick: () => navigate("/tests"),
            },
            {
                label: t("tests.delete"),
                icon: DeleteIcon,
                style: { color: "var(--secondary)" },
                onClick: () => deleteDashboardTest(test),
            },
        ]

        if (test.status === TestStatus.Public) {
            actions.unshift({
                label: t("tests.end"),
                icon: CheckIcon,
                style: { color: "var(--primary)" },
                onClick: () => completeDashboardTest(test),
            })
        }

        if (test.status === TestStatus.Public || test.status === TestStatus.Completed) {
            actions.unshift({
                label: t("tests.open"),
                icon: TestIcon,
                style: { color: "var(--tertiary)" },
                onClick: () => navigate(`/tests/${test.id}`),
            })
        }

        return actions
    }, [completeDashboardTest, deleteDashboardTest, navigate, t])

    const downloadRecording = async (recording_id, room_name) => {
        try {
            const response = await api.get(`/teacher/recordings/${recording_id}/download`)
            const link = document.createElement('a');
            link.href = response.download_link;
            link.setAttribute('download', `${room_name}.mp4`);
            document.body.appendChild(link);
            link.click();
        }
        catch (e) {
            console.error(e)
        }
    }

    const canDownloadRecordings = useMemo(() => {
        if (!context.user) {
            return false
        }
        return context.user?.permissions?.downloadRecordings
    }, [context.user])

    useEffect(() => {
        if (canDownloadRecordings) {
            getRecordings()
        }
    }, [canDownloadRecordings, getRecordings])

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Dashboard</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <div className={typo.title}>
                            {t('dashboard.title', { name: context.user?.name ?? '' })}
                        </div>
                        <div className={styles.subtitle}>
                            {t('dashboard.subtitle')}
                        </div>

                        <LessonsCarousel lessons={lessons} loading={lessonsLoading} />

                        {canDownloadRecordings &&
                            <Card>
                                <div className={styles.title}>Registrazioni</div>
                                <div className={styles.recordingsHeader}>
                                    <div className={typo.caption} style={{ textAlign: 'left' }}>LEZIONE</div>
                                    <div className={typo.caption} style={{ textAlign: 'left' }}>DATA</div>
                                    <div className={typo.caption}>DURATA</div>
                                    <div className={typo.caption}>DOWNLOAD</div>
                                </div>
                                <div className={styles.recordings}>
                                    {recordings &&
                                        <Each of={recordings.sort((a, b) => {
                                            return b.start_ts - a.start_ts
                                        })} render={(recording) => {
                                            return (
                                                <div className={styles.recording}>
                                                    <div className={styles.recordingLesson}>
                                                        {recording.lesson ? recording.lesson.name : 'Lezione'}
                                                    </div>
                                                    <div className={styles.recordingDate}>
                                                        {formatDateV2(new Date(recording.start_ts * 1000).toISOString(), { day: 'numeric', month: 'long', year: 'numeric' })} - {formatTimeV2(new Date(recording.start_ts * 1000).toISOString(), { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                    </div>
                                                    {recording.status === 'finished' &&
                                                        <div className={styles.recordingDuration}>{formatSeconds(recording.duration)}</div>
                                                    }
                                                    {recording.status === 'in-progress' &&
                                                        <div className={styles.recordingDuration}>In corso...</div>
                                                    }
                                                    <div className={styles.recordingDownload}>
                                                        <DownloadIcon className={styles.downloadIcon} onClick={() => { downloadRecording(recording.id) }} />
                                                    </div>
                                                </div>
                                            )
                                        }} />
                                    }
                                    {recordings.length === 0 && !recordingsLoading &&
                                        <div className={typo.headline}>Nessuna registrazione disponibile</div>
                                    }
                                </div>
                            </Card>
                        }

                        <div className={styles.dashboardTableSection}>
                            <div className={styles.cardContent}>
                                <div className={styles.cardTitleContainer}>
                                    <div className={styles.title}>{t("dashboard.submittedExercises")}</div>
                                    {
                                        submittedExercises.length > 0 &&
                                        <Button appearance="subtle" onClick={() => navigate("/exercises")}>{t("seeAll").toUpperCase()}</Button>
                                    }
                                </div>
                                {
                                    submittedExercisesLoading === true &&
                                    <Table
                                        appearance="minimal"
                                        loading
                                        columns={[
                                            { key: 'student_name', label: 'STUDENTE', type: 'string' },
                                            { key: 'exercise_name', label: 'ESERCIZIO', type: 'string' },
                                            { key: 'lesson_name', label: t('exercises.lesson.label'), type: 'string' },
                                            { key: 'submitted_at', label: 'CONSEGNATO IL', type: 'datetime' },
                                            { key: '_actions', label: 'AZIONI', type: 'custom' },
                                        ]}
                                        elements={[]}
                                    />
                                }

                                {
                                    !submittedExercisesLoading && submittedExercises.length > 0 &&
                                    <Table
                                        appearance="minimal"
                                        columns={[
                                            { key: 'student_name', label: 'STUDENTE', type: 'string' },
                                            { key: 'exercise_name', label: 'ESERCIZIO', type: 'string' },
                                            { key: 'lesson_name', label: t('exercises.lesson.label'), type: 'string' },
                                            { key: 'submitted_at', label: 'CONSEGNATO IL', type: 'datetime' },
                                            { key: '_actions', label: 'AZIONI', type: 'custom' },
                                        ]}
                                        defaultSort={{ key: 'submitted_at', direction: 'desc' }}
                                        elements={submittedExercises.map((submission) => ({
                                            exercise_name: submission?.exercise?.name ?? '-',
                                            student_name: `${submission?.user?.name ?? ''} ${submission?.user?.surname ?? ''}`.trim() || '-',
                                            lesson_name: submission?.lesson?.name ?? t('exercises.lesson.empty'),
                                            submitted_at: submission?.medias?.[0]?.created_at ?? submission?.created_at ?? null,
                                            _actions: {
                                                value: submission?.exercise?.id ?? 0,
                                                type: 'number',
                                                component: (
                                                    <div className={styles.actionCell}>
                                                        <MeatBallsMenu actions={getSubmittedExerciseActions(submission)} />
                                                    </div>
                                                ),
                                            },
                                        }))}
                                    />
                                }

                                {
                                    submittedExercisesLoading === false && submittedExercises.length === 0 &&
                                    <div className={styles.noData}>
                                        <EmptyContent
                                            Illustration={EmptyExercises}
                                            message={"Nessun esercizio consegnato da correggere"}
                                            action={{
                                                label: <>VAI AGLI ESERCIZI <ArrowIcon style={{ transform: "rotate(180deg)" }} /></>,
                                                onClick: () => navigate("/exercises")
                                            }}
                                        />
                                    </div>
                                }
                            </div>
                        </div>
                        <div className={styles.dashboardTableSection}>
                            <div className={styles.cardContent}>
                                <div className={styles.cardTitleContainer}>
                                    <div className={styles.title}>{t("dashboard.completedTests")}</div>
                                    {
                                        tests.length > 0 &&
                                        <Button appearance="subtle" onClick={() => navigate("/tests")}>{t("seeAll").toUpperCase()}</Button>
                                    }
                                </div>
                                <div className={styles.tests}>
                                    {
                                        testsLoading === true &&
                                        <Table
                                            appearance="minimal"
                                            loading
                                            columns={[
                                                { key: 'name', label: t('tests.name'), type: 'string' },
                                                { key: 'lesson_name', label: t('tests.lesson.label'), type: 'string' },
                                                { key: 'questions_count', label: t('tests.questions'), type: 'number' },
                                                { key: 'published_at', label: t('tests.publishedAt'), type: 'datetime' },
                                                { key: '_actions', label: 'AZIONI', type: 'custom' },
                                            ]}
                                            elements={[]}
                                        />
                                    }

                                    {
                                        !testsLoading && tests.length > 0 &&
                                        <Table
                                            appearance="minimal"
                                            columns={[
                                                { key: 'name', label: t('tests.name'), type: 'string' },
                                                { key: 'lesson_name', label: t('tests.lesson.label'), type: 'string' },
                                                { key: 'questions_count', label: t('tests.questions'), type: 'number' },
                                                { key: 'published_at', label: t('tests.publishedAt'), type: 'datetime' },
                                                { key: '_actions', label: 'AZIONI', type: 'custom' },
                                            ]}
                                            defaultSort={{ key: 'published_at', direction: 'desc' }}
                                            elements={tests.map((test) => ({
                                                ...test,
                                                lesson_name: test?.lesson?.name ?? t('tests.lesson.empty'),
                                                questions_count: getTestQuestionsCount(test),
                                                _actions: {
                                                    value: test.id,
                                                    type: 'number',
                                                    component: (
                                                        <div className={styles.actionCell}>
                                                            {!test.deleted_at && <MeatBallsMenu actions={getDashboardTestActions(test)} />}
                                                        </div>
                                                    ),
                                                },
                                            }))}
                                        />
                                    }
                                    {
                                        !testsLoading && tests.length === 0 &&
                                        <div className={styles.noData}>
                                            <EmptyContent
                                                Illustration={EmptyExercises}
                                                message={"Nessun test completato"}
                                                action={{
                                                    label: <>VAI AI TEST <ArrowIcon style={{ transform: "rotate(180deg)" }} /></>,
                                                    onClick: () => navigate("/tests")
                                                }}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </HeaderFooterLayout >
    )
}

export default Dashboard
